import { DataTable } from '@/components/table/DataTable';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Box } from '@/components/ui/box';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/drodown-menu-custom';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Multiselect } from '@/components/ui/multiselect';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { useDebounce } from '@/hooks/useDebounce';
import {
  useCreateCriteriaService,
  useDeleteCriteriaService,
  useExecuteFullTextScreeningService,
  useExportFullTextScreeningResultsService,
  useGenerateFullTextCriteriaService,
  useGetFullTextScreeningResultsService,
  useGetFullTextScreeningService,
  useUpdateCriteriaService,
} from '@/services/literatureReviews';
import { SearchBar } from '@/shared/SearchBar';
import { cn } from '@/utils/classUtils';
import { GetFullTextScreeningResultsResponse } from '@ditto/shared';
import { getPaginationRowModel } from '@tanstack/react-table';
import {
  ChevronDown,
  Crosshair,
  Download,
  List,
  ListChecks,
  Play,
  Stars,
  TriangleAlert,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import { ScreeningActionBar } from '../components/ScreeningActionBar';
import { CriteriaCard } from './components/CriteriaCard';
import { CriteriaModal } from './components/CriteriaModal';
import { DetailedModal } from './components/DetailedModal';
import { ExtractionWarningModal } from './components/ExtractionWarningModal';
import { PromptWarningModal } from './components/PromptWarningModal';
import { useColumns } from './components/Table/columns';
import { Criteria } from './types';

export const FullTextScreeningPage: FC = () => {
  const { literatureReview_id } = useParams();
  const { fullTextScreening } =
    useGetFullTextScreeningService(literatureReview_id);

  const { exportFullTextScreeningResults } =
    useExportFullTextScreeningResultsService(literatureReview_id);

  const { generateFullTextCriteria, isGenerateFullTextCriteriaPending } =
    useGenerateFullTextCriteriaService();
  const { createCriteria } = useCreateCriteriaService();
  const { updateCriteria } = useUpdateCriteriaService();
  const { deleteCriteria } = useDeleteCriteriaService();
  const { executeFullTextScreening, isExecuteFullTextScreeningPending } =
    useExecuteFullTextScreeningService();
  const handleExportFullTextScreeningResults =
    (format: 'xlsx' | 'csv') => () => {
      exportFullTextScreeningResults(format);
    };

  const [selectedPaperScreening, setSelectedPaperScreening] = useState<
    GetFullTextScreeningResultsResponse['data'][number] | undefined
  >(undefined);

  const [isPromptWarningModalOpen, setIsPromptWarningModalOpen] =
    useState(false);
  const [isExtractionWarningModalOpen, setIsExtractionWarningModalOpen] =
    useState(false);

  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState<string[]>([]);
  const [validationStatuses, setValidationStatuses] = useState<string[]>([]);
  const debouncedSearch = useDebounce(search, 300);
  const [accessType, setAccessType] = useState<string[]>([]);

  const filters = {
    ...(debouncedSearch.length > 0 && {
      searchString: debouncedSearch,
    }),
    ...(statuses.length > 0 && {
      inclusionStatus: statuses.join(','),
    }),
    ...(validationStatuses.length > 0 && {
      validationStatus: validationStatuses.join(','),
    }),
    ...(accessType.length > 0 && {
      accessType: accessType.join(','),
    }),
  };

  const accessTypeOptions = [
    { label: 'Full paper available', value: 'open_access' },
    { label: 'Full paper not available', value: 'restricted_access' },
  ];

  const hasFilters =
    debouncedSearch.length > 0 ||
    statuses.length > 0 ||
    validationStatuses.length > 0 ||
    accessType.length > 0;

  const {
    fullTextScreeningResults,
    totalCounts,
    isPending,
    isFullTextScreeningGenerating,
  } = useGetFullTextScreeningResultsService(
    literatureReview_id,
    hasFilters ? filters : undefined
  );

  const statusOptionsWithCounts = useMemo(() => {
    if (!totalCounts) {
      return [
        { label: 'Include', value: 'include' },
        { label: 'Exclude', value: 'exclude' },
        { label: 'Inconclusive', value: 'inconclusive' },
      ];
    }

    return [
      { label: `Include (${totalCounts.include})`, value: 'include' },
      { label: `Exclude (${totalCounts.exclude})`, value: 'exclude' },
      {
        label: `Inconclusive (${totalCounts.inconclusive})`,
        value: 'inconclusive',
      },
    ];
  }, [totalCounts]);

  const validationStatusOptionsWithCounts = useMemo(() => {
    if (!totalCounts) {
      return [
        { label: 'Not validated', value: 'not_validated' },
        { label: 'Validated', value: 'validated' },
      ];
    }

    return [
      {
        label: `Not validated (${totalCounts.not_validated})`,
        value: 'not_validated',
      },
      { label: `Validated (${totalCounts.validated})`, value: 'validated' },
    ];
  }, [totalCounts]);

  const [criteria, setCriteria] = useState<Criteria[]>(
    fullTextScreening?.criteria ?? []
  );

  const columns = useColumns(
    literatureReview_id,
    setSelectedPaperScreening,
    fullTextScreeningResults
  );

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const selectedRowsCount = Object.values(rowSelection).filter(Boolean).length;
  const displayActionBar = selectedRowsCount > 0;

  const clearRowSelection = () => {
    setRowSelection({});
  };

  const validatableRows = fullTextScreeningResults.filter(
    result =>
      (result.screeningResult === 'include' ||
        result.screeningResult === 'exclude') &&
      rowSelection[result.id]
  );

  const displayInconclusiveWarning = validatableRows.length < selectedRowsCount;

  const handleEditCriteria = (
    id: string,
    name: string,
    instructions: string
  ) => {
    const selectedCriteria = criteria.find(criterion => criterion.id === id);
    if (!selectedCriteria) {
      return;
    }
    const order = selectedCriteria.order;
    updateCriteria(
      {
        criteriaId: id,
        data: { name, instructions, order },
      },
      {
        onError: () => {
          toast.error('Failed to update criteria');
          setCriteria(
            criteria.map(criterion =>
              criterion.id === id ? selectedCriteria : criterion
            )
          );
        },
      }
    );
    setCriteria(
      criteria.map(criterion =>
        criterion.id === id
          ? { ...criterion, name, instructions, order }
          : criterion
      )
    );
  };

  const handleDeleteCriteria = (id: string) => {
    const selectedCriteria = criteria.find(criterion => criterion.id === id);
    if (!selectedCriteria) {
      return;
    }
    deleteCriteria(
      { criteriaId: id },
      {
        onError: () => {
          toast.error('Failed to delete the criteria');
          setCriteria(
            [...criteria, selectedCriteria].sort((a, b) => a.order - b.order)
          );
        },
      }
    );
    setCriteria(criteria.filter(criterion => criterion.id !== id));
  };

  const handleCreateCriteria = (name: string, instructions: string) => {
    const order =
      criteria.length > 0
        ? Math.max(...criteria.map(criterion => criterion.order)) + 1
        : 0;
    const screeningId = fullTextScreening?.id;
    if (!screeningId) {
      return;
    }

    createCriteria(
      {
        name,
        instructions,
        order,
        screeningId,
      },
      {
        onSuccess: data => {
          const newCriteria = {
            id: data.id,
            name: data.name,
            instructions: data.instructions,
            order: data.order,
          };

          setCriteria([...criteria, newCriteria]);
        },
        onError: () => {
          toast.error('Failed to create the criteria');
        },
      }
    );
  };

  const handleGenerateCriteria = () => {
    if (!literatureReview_id || !criteriaGenerationPrompt.trim()) {
      return;
    }
    if (displayPromptWarning) {
      setIsPromptWarningModalOpen(true);
      return;
    }
    generateFullTextCriteria(
      {
        literatureReviewId: literatureReview_id,
        data: {
          prompt: criteriaGenerationPrompt,
        },
      },
      {
        onSuccess: () => {
          toast.success('Criteria generation completed');
          setAccordionValue('criteria');
        },
        onError: () => {
          toast.error('Failed to generate criteria');
        },
      }
    );
  };

  const handlePromptWarningConfirm = () => {
    if (!literatureReview_id || !criteriaGenerationPrompt.trim()) {
      return;
    }
    generateFullTextCriteria(
      {
        literatureReviewId: literatureReview_id,
        data: {
          prompt: criteriaGenerationPrompt,
        },
      },
      {
        onSuccess: () => {
          toast.success('Criteria generation completed');
          setAccordionValue('criteria');
          setIsPromptWarningModalOpen(false);
        },
        onError: () => {
          toast.error('Failed to generate criteria');
        },
      }
    );
  };

  const handleExecuteFullTextScreening = () => {
    if (!literatureReview_id) {
      return;
    }
    if (displayTable) {
      setIsExtractionWarningModalOpen(true);
      return;
    }
    setAccordionValue('');
    executeFullTextScreening(
      {
        literatureReviewId: literatureReview_id,
        data: { criteriaIds: criteria.map(criterion => criterion.id) },
      },
      {
        onError: () => {
          toast.error('Failed to execute full text screening');
        },
      }
    );
  };

  const handleExtractionWarningConfirm = () => {
    if (!literatureReview_id) {
      return;
    }
    setAccordionValue('');
    executeFullTextScreening(
      {
        literatureReviewId: literatureReview_id,
        data: { criteriaIds: criteria.map(criterion => criterion.id) },
      },
      {
        onSuccess: () => {
          setIsExtractionWarningModalOpen(false);
        },
        onError: () => {
          toast.error('Failed to execute full text screening');
        },
      }
    );
  };

  const handleStatusChange = (selected: string[]) => {
    setStatuses(
      selected.filter(
        status =>
          status === 'include' ||
          status === 'exclude' ||
          status === 'inconclusive'
      )
    );
    setPagination(prev => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const handleValidationStatusChange = (selected: string[]) => {
    setValidationStatuses(
      selected.filter(
        status => status === 'not_validated' || status === 'validated'
      )
    );
    setPagination(prev => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const handleAccessTypeChange = (selected: string[]) => {
    setAccessType(
      selected.filter(
        accessType =>
          accessType === 'open_access' || accessType === 'restricted_access'
      )
    );
    setPagination(prev => ({
      ...prev,
      pageIndex: 0,
    }));
  };

  const displayPromptWarning = criteria.length > 0;

  const isExecutingFullTextScreening =
    isExecuteFullTextScreeningPending ||
    isFullTextScreeningGenerating ||
    isPending;

  const displayTable =
    fullTextScreeningResults.some(
      result => result.criteriaResults.length > 0
    ) ||
    isPending ||
    hasFilters ||
    fullTextScreeningResults.some(
      result => !!result.screeningResult || !!result.justification
    );

  useEffect(() => {
    setCriteria(fullTextScreening?.criteria ?? []);
    setCriteriaGenerationPrompt(
      fullTextScreening?.criteriaGenerationPrompt ?? ''
    );
  }, [fullTextScreening]);

  const [accordionValue, setAccordionValue] = useState<string | undefined>(
    'generateCriteria'
  );

  const [criteriaGenerationPrompt, setCriteriaGenerationPrompt] = useState('');
  const handleSelectNext = useCallback(() => {
    const nextIndex =
      fullTextScreeningResults.findIndex(
        result => result.id === selectedPaperScreening?.id
      ) + 1;
    if (nextIndex >= fullTextScreeningResults.length) {
      return;
    }
    setSelectedPaperScreening(fullTextScreeningResults[nextIndex]);
  }, [fullTextScreeningResults, selectedPaperScreening]);

  const handleSelectPrevious = useCallback(() => {
    const previousIndex =
      fullTextScreeningResults.findIndex(
        result => result.id === selectedPaperScreening?.id
      ) - 1;
    if (previousIndex < 0) {
      return;
    }
    setSelectedPaperScreening(fullTextScreeningResults[previousIndex]);
  }, [fullTextScreeningResults, selectedPaperScreening]);

  const isFirst = useMemo(() => {
    return (
      fullTextScreeningResults.findIndex(
        result => result.id === selectedPaperScreening?.id
      ) === 0
    );
  }, [fullTextScreeningResults, selectedPaperScreening]);
  const isLast = useMemo(() => {
    return (
      fullTextScreeningResults.findIndex(
        result => result.id === selectedPaperScreening?.id
      ) ===
      fullTextScreeningResults.length - 1
    );
  }, [fullTextScreeningResults, selectedPaperScreening]);

  return (
    <FlexCol pt="6" gap="6" h="full">
      <FlexCol px="6" gap="6" h="full" className="overflow-y-auto">
        <Text variant="header1">Full Text Screening</Text>
        <Accordion
          type="single"
          collapsible
          variant="secondary"
          value={accordionValue}
          onValueChange={setAccordionValue}
        >
          <AccordionItem value="generateCriteria">
            <AccordionTrigger className="items-center group hover:no-underline">
              <FlexCol gap="1">
                <FlexRow alignItems="center" gap="2">
                  <Crosshair className="w-4 h-4" />
                  <Text className="group-hover:underline">
                    What data do you want to extract?
                  </Text>
                </FlexRow>
                <Text variant="body-sm" color="secondary">
                  Describe the information you need from each paper. AI will
                  generate extraction fields below.
                </Text>
              </FlexCol>
            </AccordionTrigger>
            <AccordionContent>
              <FlexCol className="gap-4 items-center w-full">
                {displayPromptWarning && (
                  <FlexRow w="full" justifyContent="start">
                    <FlexRow
                      alignItems="center"
                      br="sm"
                      className="border border-orange-100 bg-orange-50"
                      gap="2"
                      px="2"
                      py="1.5"
                    >
                      <TriangleAlert
                        className="text-orange-500"
                        width="16"
                        height="16"
                      />
                      <Text variant="body-sm" color="primary">
                        Changes will regenerate fields and delete any manually
                        created or edited fields.
                      </Text>
                    </FlexRow>
                  </FlexRow>
                )}
                <FlexRow className="w-full">
                  <Textarea
                    value={criteriaGenerationPrompt}
                    onChange={e => setCriteriaGenerationPrompt(e.target.value)}
                    placeholder="Enter the data you need from each study (e.g., device type, primary outcomes, adverse events, sample demographics, inclusion/exclusion criteria, study limitations)"
                    rows={4}
                    className="bg-white w-full focus:ring-0 focus:ring-offset-0 focus:border-transparent focus:outline-none"
                  />
                </FlexRow>
                <Button
                  className="w-fit"
                  disabled={!criteriaGenerationPrompt}
                  loading={isGenerateFullTextCriteriaPending}
                  onClick={handleGenerateCriteria}
                >
                  <Stars />
                  Generate Extraction Fields
                </Button>
              </FlexCol>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="criteria">
            <AccordionTrigger className="items-center group hover:no-underline">
              <FlexCol gap="1">
                <FlexRow alignItems="center" gap="2">
                  <List className="w-4 h-4" />
                  <Text className="group-hover:underline">
                    Extraction fields
                  </Text>
                  {criteria.length > 0 && (
                    <Box
                      px="2"
                      br="xxl"
                      className="border border-blue-100 bg-blue-50 text-blue-500"
                    >
                      {criteria.length} fields configured
                    </Box>
                  )}
                </FlexRow>
                <Text variant="body-sm" color="secondary">
                  Review AI generated fields and adjust the list of data points
                  to extract from each paper.
                </Text>
              </FlexCol>
            </AccordionTrigger>
            <AccordionContent>
              <FlexCol gap="2">
                {criteria.map(criteria => (
                  <CriteriaCard
                    key={criteria.id}
                    criteria={criteria}
                    onEdit={handleEditCriteria}
                    onDelete={handleDeleteCriteria}
                  />
                ))}
                <CriteriaModal mode="create" onCreate={handleCreateCriteria} />
                <FlexRow w="full" justifyContent="center">
                  <Button
                    className="w-fit"
                    onClick={handleExecuteFullTextScreening}
                    loading={isExecutingFullTextScreening}
                  >
                    <Play />
                    Run Data Extraction
                  </Button>
                </FlexRow>
              </FlexCol>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        {displayTable && (
          <FlexCol gap="2">
            <FlexRow alignItems="center" justifyContent="between">
              <FlexRow gap="2" alignItems="center">
                <Multiselect
                  options={statusOptionsWithCounts}
                  selected={statuses}
                  onChange={handleStatusChange}
                  placeholder="Screening Decision"
                  className="w-fit text-primary"
                  trigger={
                    <Button
                      variant="outline"
                      role="combobox"
                      className={cn(
                        'justify-between font-normal w-fit text-primary',
                        statuses.length === 0 && 'text-muted-foreground'
                      )}
                    >
                      <span className="truncate">
                        {statuses.length === 0
                          ? 'Screening Decision'
                          : statuses.length === 1
                            ? statusOptionsWithCounts.find(
                                opt => opt.value === statuses[0]
                              )?.label || statuses[0]
                            : `${statuses.length} selected`}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  }
                />
                <Multiselect
                  options={validationStatusOptionsWithCounts}
                  selected={validationStatuses}
                  onChange={handleValidationStatusChange}
                  placeholder="Validation Status"
                  className="w-fit text-primary"
                  trigger={
                    <Button
                      variant="outline"
                      role="combobox"
                      className={cn(
                        'justify-between font-normal w-fit text-primary',
                        validationStatuses.length === 0 &&
                          'text-muted-foreground'
                      )}
                    >
                      <span className="truncate">
                        {validationStatuses.length === 0
                          ? 'Validation Status'
                          : validationStatuses.length === 1
                            ? validationStatusOptionsWithCounts.find(
                                opt => opt.value === validationStatuses[0]
                              )?.label || validationStatuses[0]
                            : `${validationStatuses.length} selected`}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  }
                />
                <Multiselect
                  options={accessTypeOptions}
                  selected={accessType}
                  onChange={handleAccessTypeChange}
                  placeholder="Full Paper Availability"
                  className="w-fit text-primary"
                  trigger={
                    <Button
                      variant="outline"
                      role="combobox"
                      className={cn(
                        'justify-between font-normal w-fit text-primary',
                        accessType.length === 0 && 'text-muted-foreground'
                      )}
                    >
                      <span className="truncate">
                        {accessType.length === 0
                          ? 'Full Paper Availability'
                          : accessType.length === 1
                            ? accessTypeOptions.find(
                                opt => opt.value === accessType[0]
                              )?.label || accessType[0]
                            : `${accessType.length} selected`}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  }
                />
              </FlexRow>
              <FlexRow gap="2" alignItems="center">
                <SearchBar
                  searchString={search}
                  setSearchString={setSearch}
                  placeholder="Search"
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary">
                      <Download size={16} />
                      Export Screening Results
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={handleExportFullTextScreeningResults('xlsx')}
                    >
                      Export XLSX file
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleExportFullTextScreeningResults('csv')}
                    >
                      Export CSV file
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </FlexRow>
            </FlexRow>
            <DataTable
              columns={columns}
              data={fullTextScreeningResults}
              isLoading={isPending}
              options={{
                getPaginationRowModel: getPaginationRowModel(),
                getRowId: row => row.id,
                onPaginationChange: setPagination,
                onRowSelectionChange: setRowSelection,
                autoResetPageIndex: false,
                state: {
                  pagination,
                  rowSelection,
                },
              }}
              displayTopPagination={true}
            />
            <ScreeningActionBar
              open={displayActionBar}
              clearSelection={clearRowSelection}
              validatableRows={validatableRows}
              selectedRowsCount={selectedRowsCount}
              displayInconclusiveWarning={displayInconclusiveWarning}
            />
          </FlexCol>
        )}
        {!displayTable && (
          <FlexCol className="w-full h-full pt-20">
            <FlexCol className="items-center justify-center gap-3">
              <ListChecks className="h-6 w-6" />
              <FlexCol className="items-center justify-center gap-1">
                <Text className="text-base font-medium">
                  No full text screened yet
                </Text>
                <Text className="text-sm text-neutral-500">
                  Define extraction criteria above
                </Text>
                <Text className="text-sm text-neutral-500">
                  to start begin screening.
                </Text>
              </FlexCol>
            </FlexCol>
          </FlexCol>
        )}
      </FlexCol>
      <DetailedModal
        key={selectedPaperScreening?.id}
        paperScreening={selectedPaperScreening}
        isOpen={!!selectedPaperScreening}
        onOpenChange={() => setSelectedPaperScreening(undefined)}
        selectNext={handleSelectNext}
        selectPrevious={handleSelectPrevious}
        isFirst={isFirst}
        isLast={isLast}
      />
      <PromptWarningModal
        isOpen={isPromptWarningModalOpen}
        onOpenChange={setIsPromptWarningModalOpen}
        isLoading={isGenerateFullTextCriteriaPending}
        onConfirm={handlePromptWarningConfirm}
      />
      <ExtractionWarningModal
        isOpen={isExtractionWarningModalOpen}
        onOpenChange={setIsExtractionWarningModalOpen}
        isLoading={isExecuteFullTextScreeningPending}
        onConfirm={handleExtractionWarningConfirm}
      />
    </FlexCol>
  );
};
